// src/modules/notifications/NotificationContext.jsx
//
// One store for the whole app: the bell, the /notifications page and the live
// toast all read from here. Previously the bell held its own list in local
// state, so nothing else could show a notification and read-state existed only
// inside that one component.
//
// The badge count comes from the SERVER, not from the length of the fetched
// page. The old bell counted the 50 rows it happened to have loaded, so anyone
// with more than 50 notifications simply saw a wrong number.

import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { notification as antdNotification, Modal } from 'antd';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getUnsavedCase } from '../../hooks/useUnsavedCaseGuard';
import stream from './stream';
import {
  fetchNotifications, fetchUnreadCount, markNotificationRead,
  markAllNotificationsRead, dismissNotification, dismissAllNotifications,
} from './api';

const NotificationContext = createContext(null);

const PAGE_SIZE = 25;

// Deep-link targets and the module permission that gates each one. Sending a
// user to a page they cannot open is worse than sending them to their inbox:
// PermissionRoute logs them out after two seconds.
const ROUTE_GUARDS = [
  { prefix: '/approvals', module: 'approval' },
  { prefix: '/gold-inventory', module: 'goldInventory' },
];

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { canViewModule, isWildcardAdmin } = usePermissions();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [streamStatus, setStreamStatus] = useState('idle');

  // A toast for something already on screen is noise, so the bell tells the
  // store when it is open.
  const bellOpenRef = useRef(false);
  const setBellOpen = useCallback((open) => { bellOpenRef.current = open; }, []);

  const [api, toastHolder] = antdNotification.useNotification();
  const [modal, modalHolder] = Modal.useModal();

  /**
   * Navigate to a notification's stored deep link, or to the inbox when the
   * user cannot open the target. The link itself is resolved server-side and
   * stored on the row — this never re-derives it.
   */
  const openNotification = useCallback((item) => {
    const link = item?.link;
    let target = link;
    if (!link || !link.startsWith('/')) {
      target = '/notifications';
    } else {
      const guard = ROUTE_GUARDS.find((g) => link.startsWith(g.prefix));
      if (guard && !isWildcardAdmin && !canViewModule(guard.module)) target = '/notifications';
    }

    // Leaving a half-filled new case would discard it — it exists only in the
    // form until Create. Offer a new tab instead so the case stays put.
    const unsavedCase = getUnsavedCase();
    if (unsavedCase) {
      modal.confirm({
        title: `This ${unsavedCase} has not been created yet`,
        content: 'If you leave this page now, everything entered in the case will be lost. You can open the notification in a new tab and come back to finish the case.',
        okText: 'Open in new tab',
        cancelText: 'Stay on case',
        onOk: () => { window.open(target, '_blank', 'noopener'); },
      });
      return;
    }
    navigate(target);
  }, [navigate, canViewModule, isWildcardAdmin, modal]);

  // A push notification tapped in the OS tray focuses this tab and posts the
  // click here (the Firebase SDK does it for FCM-displayed pushes, our own
  // handler in firebase-messaging-sw.js for the rest), so it takes the same
  // unsaved-case check as an in-app click instead of reloading the tab.
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : null;
    if (!sw) return undefined;
    const onMessage = (event) => {
      const msg = event.data || {};
      let link = null;
      if (msg.isFirebaseMessaging && msg.messageType === 'notification-clicked') {
        link = msg.data?.url || msg.fcmOptions?.link || null;
      } else if (msg.type === 'CRM_NOTIFICATION_CLICK') {
        link = msg.url || null;
      } else {
        return;
      }
      if (!link) return;
      // A link to another origin cannot be routed in-app; keep only the path.
      try {
        const u = new URL(link, window.location.origin);
        if (u.origin !== window.location.origin) return;
        link = `${u.pathname}${u.search}${u.hash}`;
      } catch (e) { return; }
      // A tab freshly opened on the link is already there.
      if (link === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
      openNotification({ link });
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, [openNotification]);

  const refreshCount = useCallback(async () => {
    try {
      const c = await fetchUnreadCount();
      setUnread(c.total || 0);
      setByCategory(c.byCategory || {});
    } catch (err) { /* the badge is not worth an error state */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: PAGE_SIZE });
      setItems(data.notifications || []);
      setCursor(data.nextCursor || null);
      setHasMore(!!data.hasMore);
    } catch (err) {
      console.warn('[notifications] load failed:', err?.message || err);
    } finally {
      setLoading(false);
    }
    refreshCount();
  }, [user, refreshCount]);

  const loadMore = useCallback(async () => {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: PAGE_SIZE, cursor });
      // De-duplicate: a row arriving live while a page is in flight would
      // otherwise appear twice.
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...(data.notifications || []).filter((n) => !seen.has(n.id))];
      });
      setCursor(data.nextCursor || null);
      setHasMore(!!data.hasMore);
    } catch (err) {
      console.warn('[notifications] loadMore failed:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [cursor, loading]);

  // ── Mutations. Optimistic, then reconciled against the server. ────────────

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((n) => Math.max(0, n - 1));
    try { await markNotificationRead(id); } catch (err) { /* reconciled below */ }
    refreshCount();
  }, [refreshCount]);

  const markAllRead = useCallback(async (category = null) => {
    setItems((prev) => prev.map((n) => (!category || n.category === category ? { ...n, read: true } : n)));
    try { await markAllNotificationsRead(category); } catch (err) { /* reconciled below */ }
    refreshCount();
  }, [refreshCount]);

  const dismiss = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await dismissNotification(id); } catch (err) { /* reconciled below */ }
    refreshCount();
  }, [refreshCount]);

  const dismissAll = useCallback(async () => {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    try { await dismissAllNotifications(); } catch (err) { /* reconciled below */ }
    refreshCount();
  }, [refreshCount]);

  // ── Live channel ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) { stream.disconnect(); setItems([]); setUnread(0); return undefined; }

    let token = null;
    try { token = localStorage.getItem('token'); } catch (e) { token = null; }
    if (token) stream.connect(token);

    refresh();

    const offStatus = stream.onStatus(setStreamStatus);

    const off = stream.subscribe(({ event, payload }) => {
      if (event === 'notification.created') {
        setItems((prev) => (prev.some((n) => n.id === payload.id) ? prev : [payload, ...prev]));
        setUnread((n) => n + 1);
        refreshCount();

        if (!bellOpenRef.current) {
          api.open({
            message: payload.title || 'Notification',
            description: payload.message || '',
            placement: 'bottomRight',
            duration: payload.severity === 'critical' ? 0 : 6,
            btn: payload.link ? (
              <button
                type="button"
                className="ant-btn ant-btn-primary ant-btn-sm"
                onClick={() => { api.destroy(); openNotification(payload); }}
              >
                View
              </button>
            ) : null,
          });
        }
        return;
      }

      if (event === 'notification.read') {
        setItems((prev) => prev.map((n) => (n.id === payload.id ? { ...n, read: true } : n)));
        refreshCount();
        return;
      }

      if (event === 'notification.dismissed') {
        setItems((prev) => prev.filter((n) => n.id !== payload.id));
        refreshCount();
        return;
      }

      if (event === 'notification.bulk') {
        // A bulk change is cheaper to refetch than to replay.
        refresh();
      }
    });

    return () => { off(); offStatus(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id || user?._id]);

  // A tab that was backgrounded may have missed events (the stream can be
  // dropped by the OS). Re-sync on return rather than trusting the stream.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && user) refreshCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, refreshCount]);

  const value = useMemo(() => ({
    items, unread, byCategory, loading, hasMore, streamStatus,
    refresh, loadMore, markRead, markAllRead, dismiss, dismissAll,
    openNotification, setBellOpen,
  }), [items, unread, byCategory, loading, hasMore, streamStatus,
    refresh, loadMore, markRead, markAllRead, dismiss, dismissAll, openNotification, setBellOpen]);

  return (
    <NotificationContext.Provider value={value}>
      {toastHolder}
      {modalHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside a NotificationProvider');
  return ctx;
};

export default NotificationContext;
